import { Link } from "react-router-dom";
import { Download, Sparkles, TriangleAlert } from "lucide-react";
import { TOOLBOX_DOWNLOAD_PAGE_PATH, type ToolboxUpdateNotice } from "@ttc-spectree/toolbox-contract";
import { useUpdateNotice } from "@/lib/toolbox/update-notice";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/** The instruction every band ends with: there is no self-update for an unsigned build. */
export const UPDATE_BAND_STEPS = "Download it, then close the Toolbox and run the installer. Your queue is saved.";

interface BandCopy {
  title: string;
  detail: string | undefined;
  steps: string;
  button: string;
}

/** What the band says for a notice, or null when it shows nothing. Pure; exported for tests. */
export function updateBandCopy(notice: ToolboxUpdateNotice | null): BandCopy | null {
  if (notice === null || notice.state === "none") return null;
  const version = notice.latestVersion;
  if (notice.state === "available") {
    if (version === undefined) return null;
    return { title: `Version ${version} is ready.`, detail: notice.notes, steps: UPDATE_BAND_STEPS, button: `Download ${version}` };
  }
  return {
    title: version !== undefined ? `Version ${version} is ready, and SpecTree needs it.` : "SpecTree needs a newer SpecTree Toolbox.",
    detail: notice.pausedReason,
    steps: version !== undefined ? UPDATE_BAND_STEPS : "Download the new version, then close the Toolbox and run the installer. Your queue is saved.",
    button: version !== undefined ? `Download ${version}` : "Download",
  };
}

/**
 * The "a new version is ready" band (epic PI-SPEC-SW-E288, feature
 * PI-SPEC-SW-1468, UX-PM-41, design "Getting Started Mockups" frame 1g). An
 * unsigned pilot build cannot update itself, so this is the only way fixes
 * reach the person: ToolboxFrame renders it once, under the title bar, on
 * every Toolbox page, and it has no close button.
 *
 * - `available`: blue — "Version X is ready." + the release note + how to install.
 * - `required`: amber — SpecTree needs a newer app, and what is paused until then.
 *
 * "What's new" and "Download X" open SpecTree's download page. A plain
 * browser has no bridge and shows no band.
 */
export function UpdateBand() {
  const notice = useUpdateNotice();
  const copy = updateBandCopy(notice);
  if (notice === null || copy === null) return null;
  const required = notice.state === "required";
  const Icon = required ? TriangleAlert : Sparkles;

  return (
    <div
      role={required ? "alert" : "status"}
      className={cn(
        "flex flex-none items-center gap-3 border-b px-4 py-2.5",
        TOOLBOX_TEXT.content,
        required
          ? "border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.10)] text-[#FDE68A]"
          : "border-[#1D4ED8] bg-[#1E3A8A] text-[#DBEAFE]"
      )}
      data-testid="update-band"
      data-state={notice.state}
    >
      <Icon className="h-4 w-4 flex-none" aria-hidden="true" />
      <p className="min-w-0 flex-1 leading-snug">
        <b className="font-semibold">{copy.title}</b>
        {copy.detail ? ` ${copy.detail}` : ""} {copy.steps}
      </p>
      <Link
        to={TOOLBOX_DOWNLOAD_PAGE_PATH}
        className={cn(
          "flex-none whitespace-nowrap hover:underline",
          TOOLBOX_TEXT.meta,
          required ? "text-[#FDE68A]" : "text-[#BFDBFE]"
        )}
        data-testid="update-band-whats-new"
      >
        What&rsquo;s new
      </Link>
      <Link
        to={TOOLBOX_DOWNLOAD_PAGE_PATH}
        className={cn(
          "inline-flex h-7 flex-none items-center gap-1.5 whitespace-nowrap rounded-[7px] bg-[#FAFAFA] px-3 font-semibold hover:bg-white",
          required ? "text-[#78350F]" : "text-[#1E3A8A]"
        )}
        data-testid="update-band-download"
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        {copy.button}
      </Link>
    </div>
  );
}
